import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { LoginService } from 'src/app/auth/login.service';
import { SseService } from 'src/app/core/services/sse.service';
import { HeaderService } from 'src/app/core/services/header.service';
import { DashboardFilterService } from 'src/app/core/services/dashboard-filter.service';
import { MobileMenuService } from 'src/app/core/services/mobile-menu.service';
import { WithdrawRequestService, WithdrawRequestItem } from 'src/app/core/services/withdraw-request.service';
import { NotificationItemDto, NotificationService } from 'src/app/core/services/notification.service';
import { AuthDialogComponent } from 'src/app/shared/components/auth-dialog/auth-dialog.component';
import { ProfileUpdatePayload } from 'src/app/shared/components/profile-dialog/profile-dialog.component';
import { ChangePasswordPayload } from 'src/app/shared/components/change-password-dialog/change-password-dialog.component';
import { environment } from 'src/environments/environment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BenefitService } from 'src/app/core/services/benefit.service';

type AuthTab = 'login' | 'register';

interface BankUpdatePayload {
  bankAccountName: string;
  bankName: string;
  bankBranch: string;
  bankAccountNumber: string;
  passwordBank?: string;
}

interface HeaderUser {
  displayName: string;
  email: string;
  userCode: string;
  avatar: string;
  balance: number;
}

const DEFAULT_AVATAR = 'assets/Image/default-avatar.jpg';

const DEFAULT_HEADER_USER: HeaderUser = {
  displayName: 'Chưa đăng nhập',
  email: '',
  userCode: '',
  avatar: DEFAULT_AVATAR,
  balance: 0,
};

type AfterSalesProgramDto = {
  id: number;
  userCode: string;
  description: string;
  expiredAt: string | Date;
};

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css']
})
export class HeaderComponent implements OnInit, OnDestroy {
  @ViewChild(AuthDialogComponent) authDialog?: AuthDialogComponent;

  private readonly destroy$ = new Subject<void>();
  withdrawHistory: WithdrawRequestItem[] = [];

  user: HeaderUser = { ...DEFAULT_HEADER_USER };
  showRegisterSuccessModal = false;
  dropdownVisible = false;
  walletDetailsVisible = false;
  balanceDialogVisible = false;
  withdrawSubmitting = false;
  withdrawSubmitMessage = '';
  withdrawSubmitError = false;
  bankDialogVisible = false;
  currentUserInfo: any = null;
  editProfileVisible = false;
  profileUserName = '';
  profilePhoneNumber = '';
  profileBirthDay = '';
  profileSubmitting = false;
  profileMessage = '';
  profileError = false;
  bankEditVisible = false;
  bankName = '';
  bankAccountNumber = '';
  bankAccountName = '';
  bankBranch = '';
  passwordBank = '';
  bankSubmitting = false;
  bankMessage = '';
  bankError = false;
  avatarUploading = false;
  avatarMessage = '';
  avatarError = false;
  changePasswordVisible = false;
  passwordCurrent = '';
  passwordNew = '';
  passwordConfirm = '';
  passwordSubmitting = false;
  passwordMessage = '';
  passwordError = false;
  searchKeyword = '';

  notificationsVisible = false;
  notificationsLoading = false;
  notificationsError = '';
  notifications: NotificationItemDto[] = [];
  unreadCount = 0;
  showHeaderWelcomeModal = false;
  headerWelcomeText = 'Chào mừng bạn!\nVui lòng nhấn OK để tiếp tục.';

  showAfterSalesModal = false;
  afterSalesProgramData: AfterSalesProgramDto | null = null;

  showWelcomeConfetti = false;
  private confettiTimer?: ReturnType<typeof setTimeout>;
  private hasShownWelcomeModal = false; // Flag để tracking nếu đã hiển thị modal trong session này

  constructor(
    private loginService: LoginService,
    private headerService: HeaderService,
    private mobileMenuService: MobileMenuService,
    private withdrawRequestService: WithdrawRequestService,
    private notificationService: NotificationService,
    private dashboardFilterService: DashboardFilterService,
    private router: Router,
    private benefitService: BenefitService,
    private sseService: SseService
  ) {}

  ngOnInit(): void {
    if (this.isLoggedIn) {
      this.loadCurrentUser();
      this.loadAfterSalesProgram();
    }

    this.headerService.refresh$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadCurrentUser();
        this.loadAfterSalesProgram();
      });

    // Mỗi lần Header được tạo lại thì hiện modal
    this.showHeaderWelcomeModal = true;

    // Load chương trình hậu đãi và bật modal nếu có data
    // (đăng nhập sau khi Header đã mount thì sẽ gọi ở onLoggedIn/refresh$)

    if (this.loginService.isLoggedIn()) {
      const ud = this.loginService.getUserDetail();
      const userId = Number(ud?.id || ud?.userId || ud?.conversationId || 0);
      if (userId) this.sseService.connect(userId);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.confettiTimer) clearTimeout(this.confettiTimer);
  }

  openAuthDialog(tab: AuthTab): void {
    this.authDialog?.open(tab);
  }

  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    this.searchKeyword = input?.value ?? '';
  }

  onSearchSubmit(): void {
    const keyword = (this.searchKeyword || '').trim();
    this.dashboardFilterService.setKeyword(keyword);
  }

  clearSearch(): void {
    this.searchKeyword = '';
    this.dashboardFilterService.setKeyword('');
  }

  toggleDropdown(): void {
    this.balanceDialogVisible = false;
    this.notificationsVisible = false;
    this.dropdownVisible = !this.dropdownVisible;
    if (!this.dropdownVisible) {
      this.walletDetailsVisible = false;
    }
  }

  toggleNotifications(event?: Event): void {
    event?.stopPropagation();
    this.dropdownVisible = false;
    this.walletDetailsVisible = false;
    this.balanceDialogVisible = false;
    this.bankDialogVisible = false;
    this.notificationsVisible = !this.notificationsVisible;

    if (this.notificationsVisible) {
    this.fetchNotifications();
    this.notificationService.markAllAsRead().subscribe({
      next: () => {
        this.notifications.forEach(n => n.isRead = true);
        this.unreadCount = 0;
      }
    });
  }
  }

  onOpenNotification(item: NotificationItemDto): void {
    if (!item?.id) return;
    if (!item.isRead) {
      this.notificationService.markAsRead(item.id).subscribe({
        next: () => {
          item.isRead = true;
          this.unreadCount = Math.max(0, this.unreadCount - 1);
        }
      });
    }
  }

  getNotificationTitle(item: NotificationItemDto): string {
    const title = (item?.title ?? item?.message ?? 'Thông báo') as any;
    return String(title || 'Thông báo');
  }

  getNotificationSubtitle(item: NotificationItemDto): string {
    const subtitle = item?.message;
    if (!subtitle) return '';
    const subtitleText = String(subtitle).trim();
    const titleText = String(item?.title ?? '').trim();
    if (!subtitleText || subtitleText === titleText) return '';
    return subtitleText;
  }

  getNotificationTime(item: NotificationItemDto): string {
    const raw = (item?.createDate ?? item?.createdAt ?? item?.createdDate ?? item?.time ?? '') as any;
    if (!raw) return '';
    const date = new Date(raw);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleString('vi-VN');
    }
    return String(raw);
  }

  getNotificationAvatarUrl(item: NotificationItemDto): string | null {
    return this.resolveAssetUrl(item?.imageUrl ?? item?.icon ?? null);
  }

  getNotificationAvatarText(item: NotificationItemDto): string {
    const source = String(item?.sender ?? item?.createUser ?? item?.title ?? '').trim();
    if (!source) return 'N';
    return source.charAt(0).toUpperCase();
  }

  private loadUnreadCount(): void {
  this.notificationService.getUnreadNotifications()
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (items) => {
        this.unreadCount = Array.isArray(items) ? items.length : 0;
      },
      error: () => {
        this.unreadCount = 0;
      }
    });
}
  private fetchNotifications(): void {
    this.notificationsLoading = true;
    this.notificationsError = '';

    this.notificationService.getNotifications()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (items) => {
          this.notificationsLoading = false;
          this.notificationsError = '';
          this.notifications = Array.isArray(items) ? items : [];
          this.unreadCount = this.notifications.filter(n => !n.isRead).length;
        },
        error: () => {
          this.notificationsLoading = false;
          this.notifications = [];
          this.notificationsError = 'Không tải được thông báo. Vui lòng thử lại.';
        }
      });
  }

  openBalanceDialog(event?: Event): void {
    event?.stopPropagation();
    this.notificationsVisible = false;
    this.dropdownVisible = false;
    this.walletDetailsVisible = false;
    this.bankDialogVisible = false;
    this.withdrawSubmitMessage = '';
    this.withdrawSubmitError = false;
    this.balanceDialogVisible = true;
    this.loadCurrentUser();
  }

  closeBalanceDialog(): void {
    this.balanceDialogVisible = false;
    this.withdrawSubmitting = false;
    this.withdrawSubmitMessage = '';
    this.withdrawSubmitError = false;
  }

  submitWithdrawRequest(payload: { amount: number; password: string }): void {
    const amount = payload?.amount ?? 0;
    const passwordBank = payload?.password || '';
    const bankName = this.currentUserInfo?.bankName || '';
    const bankAccount = this.currentUserInfo?.bankAccountNumber || this.currentUserInfo?.bankAccount || '';
    const accountName = this.currentUserInfo?.bankAccountName || this.currentUserInfo?.userCode || this.user.displayName || '';

    if (!bankName || !bankAccount || !accountName) {
      this.withdrawSubmitError = true;
      this.withdrawSubmitMessage = 'Thiếu thông tin ngân hàng. Vui lòng cập nhật tài khoản ngân hàng trước khi rút tiền.';
      return;
    }

    this.withdrawSubmitting = true;
    this.withdrawSubmitError = false;
    this.withdrawSubmitMessage = '';

    this.withdrawRequestService.createWithdrawRequest({
      amount,
      note: ``,
      bankName,
      bankAccount,
      accountName,
      passwordBank
    }).subscribe({
      next: () => {
        this.withdrawSubmitting = false;
        this.withdrawSubmitError = false;
        this.withdrawSubmitMessage = 'Đã gửi yêu cầu rút tiền thành công. Vui lòng chờ duyệt.';
        // refresh withdraw history immediately so dialog shows the new request
        const uid = Number(this.currentUserInfo?.id ?? 0);
        if (uid > 0) {
          this.withdrawRequestService.getHistoryRequests(uid).subscribe({
            next: (rows) => {
              this.withdrawHistory = Array.isArray(rows) ? rows : [];
            },
            error: () => {
              // keep existing history if refresh fails
            }
          });
        }
      },
      error: () => {
        this.withdrawSubmitting = false;
        this.withdrawSubmitError = true;
        this.withdrawSubmitMessage = 'Gửi yêu cầu rút tiền thất bại. Vui lòng nhập lại mật khẩu rút tiền. ';
      }
    });
  }

  openBankDialog(event?: Event): void {
    event?.stopPropagation();
    this.notificationsVisible = false;
    this.balanceDialogVisible = false;
    this.dropdownVisible = false;
    this.walletDetailsVisible = false;
    this.bankDialogVisible = true;
    this.loadCurrentUser();
  }

  closeBankDialog(): void {
    this.bankDialogVisible = false;
  }

  submitBankDialog(payload: BankUpdatePayload): void {
    this.bankName = payload.bankName;
    this.bankAccountNumber = payload.bankAccountNumber;
    this.bankAccountName = payload.bankAccountName;
    this.bankBranch = payload.bankBranch || '';
    this.passwordBank = payload.passwordBank || '';
    this.submitBankUpdate();
  }

  toggleWalletDetails(): void {
    this.walletDetailsVisible = !this.walletDetailsVisible;
  }

  openEditProfile(): void {
    this.editProfileVisible = true;
    this.profileMessage = '';
    this.profileError = false;
    if (!this.currentUserInfo) {
      this.loadCurrentUser();
    }
    this.profileUserName = this.currentUserInfo?.userName || this.user.displayName || '';
    this.profilePhoneNumber = this.currentUserInfo?.phoneNumber || '';
    this.profileBirthDay = this.currentUserInfo?.birthDay || '';
    this.changePasswordVisible = false;
    this.bankEditVisible = false;
  }

  closeEditProfile(): void {
    this.editProfileVisible = false;
    this.profileMessage = '';
    this.profileError = false;
  }

  openBankEdit(): void {
    this.bankEditVisible = true;
    this.bankMessage = '';
    this.bankError = false;
    if (!this.currentUserInfo) {
      this.loadCurrentUser();
    }
    this.bankName = this.currentUserInfo?.bankName || '';
    this.bankAccountNumber = this.currentUserInfo?.bankAccountNumber || '';
    this.bankAccountName = this.currentUserInfo?.bankAccountName || '';
    this.bankBranch = this.currentUserInfo?.bankBranch || '';
    this.passwordBank = this.currentUserInfo?.passwordBank || '';
    this.editProfileVisible = false;
    this.changePasswordVisible = false;
  }

  closeBankEdit(): void {
    this.bankEditVisible = false;
    this.bankMessage = '';
    this.bankError = false;
  }

  openChangePassword(): void {
    this.changePasswordVisible = true;
    this.passwordMessage = '';
    this.passwordError = false;
    this.editProfileVisible = false;
    this.bankEditVisible = false;
  }

  closeChangePassword(): void {
    this.changePasswordVisible = false;
    this.passwordMessage = '';
    this.passwordError = false;
    this.passwordCurrent = '';
    this.passwordNew = '';
    this.passwordConfirm = '';
  }

  handleProfileDialogSubmit(payload: ProfileUpdatePayload): void {
    this.profileUserName = payload.userName;
    this.profilePhoneNumber = payload.phoneNumber;
    this.profileBirthDay = payload.birthDay || '';
    this.submitEditProfile();
  }

  handleChangePasswordSubmit(payload: ChangePasswordPayload): void {
    this.passwordCurrent = payload.passwordCurrent;
    this.passwordNew = payload.passwordNew;
    this.passwordConfirm = payload.passwordConfirm;
    this.submitChangePassword();
  }

  submitBankUpdate(): void {
    if (!this.bankName || !this.bankAccountNumber || !this.bankAccountName) {
      this.bankError = true;
      this.bankMessage = 'Vui lòng nhập đủ thông tin ngân hàng.';
      return;
    }

    this.bankSubmitting = true;
    this.bankError = false;
    this.bankMessage = '';

    const payload = {
      id: this.currentUserInfo?.id ?? null,
      userCode: this.currentUserInfo?.userCode || this.user.userCode || '',
      userName: this.currentUserInfo?.userName || this.user.displayName || '',
      phoneNumber: this.currentUserInfo?.phoneNumber || '',
      passwordCurrent: null,
      password: null,
      remark: this.currentUserInfo?.remark || '',
      avatar: this.currentUserInfo?.avatar || this.user.avatar || '',
      isOnNotification: this.currentUserInfo?.isOnNotification ?? null,
      birthDay: this.currentUserInfo?.birthDay || null,
      bankName: this.bankName,
      bankAccountNumber: this.bankAccountNumber,
      bankAccountName: this.bankAccountName,
      bankBranch: this.bankBranch || '',
      passwordBank: this.passwordBank || '',
    };

    this.headerService.updateUser(payload).subscribe({
      next: () => {
        this.bankSubmitting = false;
        this.bankError = false;
        this.bankMessage = 'Cập nhật tài khoản ngân hàng thành công.';
        this.loadCurrentUser();
        this.dropdownVisible = false;
        this.walletDetailsVisible = false;
        this.bankDialogVisible = false;
        this.bankEditVisible = false;
        this.router.navigate(['/dashboard/stocks']);
      },
      error: (err) => {
        this.bankSubmitting = false;
        this.bankError = true;
        this.bankMessage = err?.error?.message || 'Cập nhật tài khoản ngân hàng thất bại. Vui lòng thử lại.';
      }
    });
  }

  submitEditProfile(): void {
    if (!this.profileUserName) {
      this.profileError = true;
      this.profileMessage = 'Vui lòng nhập họ tên.';
      return;
    }

    this.profileSubmitting = true;
    this.profileError = false;
    this.profileMessage = '';

    const payload = {
      id: this.currentUserInfo?.id ?? null,
      userName: this.profileUserName,
      phoneNumber: this.profilePhoneNumber || '',
      passwordCurrent: null,
      password: null,
      remark: this.currentUserInfo?.remark || '',
      avatar: this.currentUserInfo?.avatar || this.user.avatar || '',
      isOnNotification: this.currentUserInfo?.isOnNotification ?? null,
      birthDay: this.profileBirthDay || null,
      bankName: this.currentUserInfo?.bankName || '',
      bankAccountNumber: this.currentUserInfo?.bankAccountNumber || '',
      bankAccountName: this.currentUserInfo?.bankAccountName || '',
      bankBranch: this.currentUserInfo?.bankBranch || '',
    };

    this.headerService.updateUser(payload).subscribe({
      next: () => {
        this.profileSubmitting = false;
        this.profileError = false;
        this.profileMessage = 'Cập nhật hồ sơ thành công.';
        this.user.displayName = this.profileUserName;
        this.loadCurrentUser();
        // Dialog will remain open; parent controls closing now.
      },
      error: (err) => {
        this.profileSubmitting = false;
        this.profileError = true;
        this.profileMessage = err?.error?.message || 'Cập nhật hồ sơ thất bại. Vui lòng thử lại.';
      }
    });
  }

  submitChangePassword(): void {
    if (!this.passwordCurrent || !this.passwordNew || !this.passwordConfirm) {
      this.passwordError = true;
      this.passwordMessage = 'Vui lòng nhập mật khẩu hiện tại và mật khẩu mới.';
      return;
    }

    if (this.passwordNew !== this.passwordConfirm) {
      this.passwordError = true;
      this.passwordMessage = 'Mật khẩu mới không khớp.';
      return;
    }

    this.passwordSubmitting = true;
    this.passwordError = false;
    this.passwordMessage = '';

    const payload = {
      id: this.currentUserInfo?.id ?? null,
      userName: this.currentUserInfo?.userName || this.user.displayName || '',
      phoneNumber: this.currentUserInfo?.phoneNumber || '',
      passwordCurrent: this.passwordCurrent,
      password: this.passwordNew,
      remark: this.passwordConfirm,
      avatar: this.currentUserInfo?.avatar || this.user.avatar || '',
      isOnNotification: this.currentUserInfo?.isOnNotification ?? null,
      birthDay: this.currentUserInfo?.birthDay || null,
      bankName: this.currentUserInfo?.bankName || '',
      bankAccountNumber: this.currentUserInfo?.bankAccountNumber || '',
      bankAccountName: this.currentUserInfo?.bankAccountName || '',
      bankBranch: this.currentUserInfo?.bankBranch || '',
    };

    this.headerService.updateUser(payload).subscribe({
      next: () => {
        this.passwordSubmitting = false;
        this.passwordError = false;
        this.passwordMessage = 'Đổi mật khẩu thành công.';
        this.passwordCurrent = '';
        this.passwordNew = '';
        this.passwordConfirm = '';
        this.dropdownVisible = false;
        this.walletDetailsVisible = false;
        this.changePasswordVisible = false;
        this.router.navigate(['/dashboard/stocks']);
      },
      error: (err) => {
        this.passwordSubmitting = false;
        this.passwordError = true;
        this.passwordMessage = err?.error?.message || 'Đổi mật khẩu thất bại. Vui lòng thử lại.';
      }
    });
  }

  onAvatarSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    this.uploadAvatar(file);
    input.value = '';
  }

  openAvatarPreview(): void {
    if (!this.user?.avatar) {
      return;
    }
    window.open(this.user.avatar, '_blank');
  }

  private uploadAvatar(file: File): void {
    this.avatarUploading = true;
    this.avatarError = false;
    this.avatarMessage = '';

    this.headerService.updateAvatar(file).subscribe({
      next: (res: any) => {
        this.avatarUploading = false;
        this.avatarError = false;
        this.avatarMessage = '';
        const newAvatar = res?.data?.avatar || res?.avatar || res?.data || null;
        if (typeof newAvatar === 'string' && newAvatar) {
          this.user.avatar = this.resolveAvatarUrl(newAvatar);
        }
        this.loadCurrentUser();
      },
      error: (err) => {
        this.avatarUploading = false;
        this.avatarError = true;
        this.avatarMessage = err?.error?.message || 'Cập nhật avatar thất bại. Vui lòng thử lại.';
      }
    });
  }

  toggleMobileMenu(): void {
    this.mobileMenuService.toggle();
  }

  logout(): void {
    this.dropdownVisible = false;
    this.walletDetailsVisible = false;
    this.balanceDialogVisible = false;
    this.notificationsVisible = false;
    this.withdrawSubmitting = false;
    this.withdrawSubmitMessage = '';
    this.withdrawSubmitError = false;
    this.bankDialogVisible = false;
    this.editProfileVisible = false;
    this.profileSubmitting = false;
    this.profileMessage = '';
    this.profileError = false;
    this.bankEditVisible = false;
    this.bankSubmitting = false;
    this.bankMessage = '';
    this.bankError = false;
    this.avatarUploading = false;
    this.avatarMessage = '';
    this.avatarError = false;
    this.changePasswordVisible = false;
    this.passwordSubmitting = false;
    this.passwordMessage = '';
    this.passwordError = false;
    this.passwordConfirm = '';
    this.sseService.disconnect();
    this.loginService.logout();
    this.user = { ...DEFAULT_HEADER_USER };
    this.currentUserInfo = null;
    window.location.href = '/dashboard/stocks';
  }

  reloadMediaList(): void {
    const url = '/dashboard/stocks';
    const currentUrl = this.router.url || '';
    if (currentUrl === url) {
      this.scrollToTop();
      return;
    }

    this.router.navigateByUrl(url).then(() => {
      this.scrollToTop();
    });
  }

  onAlibabaClick(): void {
    this.reloadMediaList();
  }

  scrollToTop(): void {
    const mainContent = document.querySelector<HTMLElement>('.main-content');
    if (mainContent && mainContent.scrollTop !== undefined) {
      mainContent.scrollTop = 0;
    }

    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    window.scrollTo(0, 0);
  }

  closeHeaderWelcomeModal(): void {
    this.showHeaderWelcomeModal = false;
  }

  closeAfterSalesModal(): void {
    const programId = this.afterSalesProgramData?.id;

    this.showAfterSalesModal = false;
    this.afterSalesProgramData = null;
    this.showWelcomeConfetti = false;

    if (this.confettiTimer) {
      clearTimeout(this.confettiTimer);
      this.confettiTimer = undefined;
    }

    // Call API updateStatus => status: 'readed' khi bấm OK (đóng modal)
    if (programId != null) {
      this.benefitService.updateStatus({ id: programId, status: 'readed' } as any).subscribe({
        next: () => {
          // no-op
        },
        error: () => {
          // no-op (không block UI)
        }
      });
    }
  }

  get isLoggedIn(): boolean {
    return this.loginService.isLoggedIn();
  }

  onLoggedIn(): void {
    this.loadCurrentUser();
    this.loadAfterSalesProgram();
  }

  private loadAfterSalesProgram(): void {
    if (!this.isLoggedIn) {
      return;
    }

    this.benefitService.getAfterSalesProgram().pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        const data: any | null = res?.data ?? res ?? null;
        if (!data || !data.id) {
          return;
        }

        this.afterSalesProgramData = data;
        this.showAfterSalesModal = true;

        this.showWelcomeConfetti = true;
        if (this.confettiTimer) {
          clearTimeout(this.confettiTimer);
        }

        this.confettiTimer = setTimeout(() => {
          this.showWelcomeConfetti = false;
        }, 60000);
      },
      error: () => {
        // im lặng: không hiện modal nếu API lỗi
      }
    });
  }

  get formattedBalance(): string {
    return new Intl.NumberFormat('vi-VN').format(Number(this.user.balance || 0));
  }

  get isMobileView(): boolean {
    return window.innerWidth <= 768;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    if (this.notificationsVisible && !target.closest('.notification-menu')) {
      this.notificationsVisible = false;
    }

    if (this.isMobileView || !this.dropdownVisible) {
      return;
    }

    if (!target.closest('.user-menu')) {
      this.dropdownVisible = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.dropdownVisible = false;
    this.walletDetailsVisible = false;
    this.balanceDialogVisible = false;
    this.notificationsVisible = false;
    this.withdrawSubmitting = false;
    this.withdrawSubmitMessage = '';
    this.withdrawSubmitError = false;
    this.bankDialogVisible = false;
  }

  private loadCurrentUser(): void {
    this.loadUnreadCount();
    this.headerService.getCurrentUser().subscribe({
      next: (data: any) => {
        this.currentUserInfo = data || null;
        this.loginService.saveRole(data?.role || '');
        this.user.displayName = data?.userName || data?.userCode || 'Người dùng';
        this.user.email = data?.email || '';
        this.user.userCode = data?.userCode || '';
        this.user.balance = Number(data?.balence ?? data?.balance ?? 0);
        if (data?.avatar) {
          this.user.avatar = this.resolveAvatarUrl(data.avatar);
        } else {
          this.user.avatar = DEFAULT_AVATAR;
        }
        // load withdraw history for current user
        const userId = Number(this.currentUserInfo?.id ?? 0);
        if (userId > 0) {
          this.withdrawRequestService.getHistoryRequests(userId).subscribe({
            next: (rows) => {
              this.withdrawHistory = Array.isArray(rows) ? rows : [];
            },
            error: () => {
              this.withdrawHistory = [];
            }
          });
        } else {
          this.withdrawHistory = [];
        }
      },
      error: () => {
        this.currentUserInfo = null;
        this.loginService.saveRole('');
        this.user = { ...DEFAULT_HEADER_USER };
      }
    });
  }

  private resolveAssetUrl(path: string | null | undefined): string | null {
    if (!path) {
      return null;
    }
    const normalizedPath = String(path).replace(/\\/g, '/');
    if (/^https?:\/\//i.test(normalizedPath) || normalizedPath.startsWith('data:')) {
      return normalizedPath;
    }
    const baseUrl = environment.apiUrl?.replace(/\/$/, '') || '';
    const cleanedPath = normalizedPath.replace(/^\//, '');
    return baseUrl ? `${baseUrl}/${cleanedPath}` : cleanedPath;
  }

  private resolveAvatarUrl(path: string): string {
    return this.resolveAssetUrl(path) || DEFAULT_AVATAR;
  }

  openRegisterSuccessModal(isFirstLogin: boolean = false): void {
    if (!isFirstLogin || this.hasShownWelcomeModal) return;

    this.headerService.getCurrentUser().subscribe({
      next: (userInfo: any) => {
        const userCreateDate = userInfo?.createDate || userInfo?.createdDate;
        if (!userCreateDate) return;

        const timeDiffMinutes = (new Date().getTime() - new Date(userCreateDate).getTime()) / (1000 * 60);
        if (timeDiffMinutes > 2) return; // 2 phút

        this.hasShownWelcomeModal = true;
        this.showRegisterSuccessModal = true;
        this.showWelcomeConfetti = true;

        if (this.confettiTimer) clearTimeout(this.confettiTimer);
        this.confettiTimer = setTimeout(() => this.showWelcomeConfetti = false, 5000);
      }
    });
  }

  closeRegisterSuccessModal(): void {
    this.showRegisterSuccessModal = false;
    this.showWelcomeConfetti = false;
    if (this.confettiTimer) {
      clearTimeout(this.confettiTimer);
    }
  }
}
